import { wrapTextToLines } from './wordWrap';

/**
 * Deterministic text layout — word-wrap inside the frame, then shrink font if still too tall.
 * @param {import('../sceneGraph/schema').SceneNode} node
 * @param {(typography: object, line: string) => number} measureLineWidth
 */
export function layoutTextNode(node, measureLineWidth) {
  if (node.type !== 'text') return node;

  const frame = node.transform;
  const typography = { ...node.typography };
  const constraints = node.constraints || {};
  const pad = constraints.padding ?? 2;
  const innerW = Math.max(1, frame.width - pad * 2);
  const innerH = Math.max(1, frame.height - pad * 2);
  const baseFs = Math.max(constraints.minFontSize || 6, typography.fontSize || 12);
  typography.fontSize = Math.min(constraints.maxFontSize || 9999, baseFs);

  const measure = (line) => measureLineWidth(typography, line);
  const wrapAt = (w) => wrapTextToLines(node.text || '', w, measure);

  let lines = wrapAt(innerW);
  let lh = (typography.lineHeight || 1.16) * typography.fontSize;

  const recompute = () => {
    const contentWidth = Math.max(0, ...lines.map(measure), 0);
    const contentHeight = Math.max(lh, lines.length * lh);
    return { contentWidth, contentHeight };
  };

  let { contentWidth, contentHeight } = recompute();
  let scale = 1;
  let fontSize = typography.fontSize;

  const layoutMode = constraints.layoutMode || 'fixedFrame';
  const overflow = constraints.overflowStrategy || 'wrap';

  if (layoutMode === 'autoHeight') {
    frame.height = Math.max(frame.height, contentHeight + pad * 2);
  } else if (layoutMode === 'autoWidth') {
    frame.width = Math.max(frame.width, contentWidth + pad * 2);
  } else {
    const maxLines = () => Math.max(1, Math.floor(innerH / lh));

    if (overflow === 'truncate' || overflow === 'ellipsis') {
      const cap = maxLines();
      if (lines.length > cap) {
        lines = lines.slice(0, cap);
        if (overflow === 'ellipsis' && lines.length) {
          const last = lines[lines.length - 1];
          lines[lines.length - 1] = last.length > 1 ? `${last.slice(0, Math.max(0, last.length - 1))}…` : '…';
        }
      }
      ({ contentWidth, contentHeight } = recompute());
    } else if (overflow === 'clip') {
      const cap = maxLines();
      if (lines.length > cap) lines = lines.slice(0, cap);
      ({ contentWidth, contentHeight } = recompute());
    } else {
      // wrap | scale | scaleDown — multi-line word wrap first, then shrink to fit box height
      const needsFit = () => contentHeight > innerH + 0.5 || contentWidth > innerW + 0.5;
      if (needsFit()) {
        let fs = typography.fontSize;
        const minFs = constraints.minFontSize || 6;
        for (let i = 0; i < 16 && needsFit(); i++) {
          const scaleH = innerH / Math.max(lh, contentHeight);
          const scaleW = innerW / Math.max(1, contentWidth);
          const fit = Math.min(1, scaleH, scaleW) * 0.985;
          if (fit >= 0.999) break;
          fs = Math.max(minFs, fs * fit);
          if (fs <= minFs + 0.01) break;
          typography.fontSize = fs;
          fontSize = fs;
          lh = (typography.lineHeight || 1.16) * fs;
          lines = wrapAt(innerW);
          ({ contentWidth, contentHeight } = recompute());
          scale = fs / baseFs;
        }
        if (contentHeight > innerH + 0.5) {
          const cap = maxLines();
          if (lines.length > cap) lines = lines.slice(0, cap);
          ({ contentWidth, contentHeight } = recompute());
        }
      }
    }
    contentHeight = Math.min(contentHeight, innerH);
    contentWidth = Math.min(contentWidth, innerW);
  }

  // Preserve the user's intended typography (fontFamily, fontSize, fontWeight, fontStyle,
  // underline, etc.) — only the rendered/scaled font size lives in layout.fontSize.
  // This prevents save→reload cycles from silently overwriting the design-intent font size.
  node.layout = {
    lines,
    fontSize,
    contentWidth,
    contentHeight,
    scale: scale < 1 ? scale : undefined,
  };
  return node;
}

export function layoutSceneGraph(scene, measureLineWidth) {
  if (!scene?.objects) return scene;
  for (const obj of scene.objects) {
    if (obj.type === 'text') layoutTextNode(obj, measureLineWidth);
  }
  return scene;
}
